import { useState, useEffect, useCallback } from 'react';
import {
  fetchPerformanceHistory,
  reduceByTimeFrame,
  ChartDataPoint,
  ChartClearRecord,
  TimeFrameOption,
  GroupByOption,
} from '../../../services/marketPerformanceService';
import type { ChartStatsData } from '../MarketPerformanceChart';

interface UseMarketPerformanceChartParams {
  selectedTimeFrame: TimeFrameOption;
  selectedGroupBy: GroupByOption;
  currency: string;
  marketUuid?: string;
  isCustomRangeSelected: boolean;
  customRange: { startDate: Date | null; endDate: Date | null };
  onStatsChange?: (stats: ChartStatsData) => void;
  onError?: (error: Error) => void;
}

export const useMarketPerformanceChart = ({
  selectedTimeFrame,
  selectedGroupBy,
  currency,
  marketUuid,
  isCustomRangeSelected,
  customRange,
  onStatsChange,
  onError,
}: UseMarketPerformanceChartParams) => {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [rawRecords, setRawRecords] = useState<ChartClearRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate =
        isCustomRangeSelected && customRange.startDate ? customRange.startDate : selectedTimeFrame.startDate;
      const endDate = isCustomRangeSelected && customRange.endDate ? customRange.endDate : selectedTimeFrame.endDate;

      const records = await fetchPerformanceHistory(startDate, currency, endDate, marketUuid);
      setRawRecords(records);

      const chartData = reduceByTimeFrame(records, selectedGroupBy.value, currency);
      setData(chartData);

      if (onStatsChange) {
        const totalInvoiceAmount = chartData.reduce((sum, p) => sum + p.clearedInvoiceAmount, 0);
        const weightedYieldSum = chartData.reduce((sum, p) => sum + p.yield * p.clearedInvoiceAmount, 0);

        const totals = {
          clearedEarn: chartData.reduce((sum, p) => sum + p.revenue, 0),
          clearedYield: totalInvoiceAmount > 0 ? Math.round((weightedYieldSum / totalInvoiceAmount) * 100) / 100 : 0,
          clearedInvoiceAmount: totalInvoiceAmount,
          clearedTakerCount: chartData.reduce((sum, p) => sum + p.divisionsAwarded, 0),
        };

        const label = isCustomRangeSelected
          ? endDate
            ? `${startDate.toISOString().split('T')[0]} - ${endDate.toISOString().split('T')[0]}`
            : `Since ${startDate.toISOString().split('T')[0]}`
          : selectedTimeFrame.label;

        onStatsChange({
          ...totals,
          startDate,
          endDate,
          label,
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load chart data');
      setError(error.message);
      onError?.(error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedTimeFrame,
    selectedGroupBy.value,
    currency,
    marketUuid,
    onStatsChange,
    onError,
    isCustomRangeSelected,
    customRange,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetchData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const updateDataForGroupBy = useCallback(
    (groupBy: GroupByOption) => {
      if (rawRecords.length > 0) {
        const chartData = reduceByTimeFrame(rawRecords, groupBy.value, currency);
        setData(chartData);
      }
    },
    [rawRecords, currency],
  );

  return {
    data,
    rawRecords,
    loading,
    error,
    refetchData,
    updateDataForGroupBy,
  };
};
